import type {
  ProductDetailProduct,
  ProductDetailVariant,
} from "@/services/product/get-product-detail";

type ProductLike = ProductDetailProduct | ProductDetailVariant;
type ProductStockEntry = NonNullable<ProductDetailVariant["stocks"]>[number];

export type ProductDetailStockStatus =
  | "inStock"
  | "availableInFuture"
  | "outOfStock"
  | "unknown";

function isPositiveStockLevel(stock: { stockLevel?: number }): boolean {
  return typeof stock.stockLevel === "number" && stock.stockLevel > 0;
}

function getStockEntries(product: ProductLike): ProductStockEntry[] {
  return "stocks" in product ? (product.stocks ?? []) : [];
}

export function getMaxStockLevel(product: ProductLike): number | undefined {
  const stockEntries = getStockEntries(product);
  if (stockEntries.length > 0) {
    return Math.max(...stockEntries.map((s) => s.stockLevel ?? 0));
  }
  const level = product.stock?.stockLevel;
  return typeof level === "number" ? level : undefined;
}

function getStockStatusFromValue(
  value: string | null | undefined
): ProductDetailStockStatus | undefined {
  const normalizedValue = value?.toLowerCase();

  if (!normalizedValue) {
    return;
  }

  if (
    normalizedValue.includes("immediate") ||
    normalizedValue.includes("instock") ||
    normalizedValue.includes("in_stock") ||
    normalizedValue.includes("lowstock") ||
    normalizedValue.includes("low_stock")
  ) {
    return "inStock";
  }

  if (normalizedValue.includes("future")) {
    return "availableInFuture";
  }

  if (
    normalizedValue.includes("unavailable") ||
    normalizedValue.includes("outofstock") ||
    normalizedValue.includes("out_of_stock")
  ) {
    return "outOfStock";
  }

  return "unknown";
}

export function getProductDetailStockStatus(
  product: ProductLike
): ProductDetailStockStatus | undefined {
  const stockEntries = getStockEntries(product);

  if (
    stockEntries.some(
      (stock) =>
        isPositiveStockLevel(stock) &&
        getStockStatusFromValue(stock.stockLevelType) === "inStock"
    )
  ) {
    return "inStock";
  }

  if (
    stockEntries.some(
      (stock) =>
        getStockStatusFromValue(stock.stockLevelType) === "availableInFuture"
    )
  ) {
    return "availableInFuture";
  }

  const stock = product.stock;

  if (!stock) {
    return stockEntries.length > 0 ? "outOfStock" : undefined;
  }

  const stockStatus =
    getStockStatusFromValue(stock.stockLevelType) ??
    getStockStatusFromValue(stock.stockLevelStatus);

  if (stockStatus && stockStatus !== "unknown") {
    return stockStatus;
  }

  if (isPositiveStockLevel(stock)) {
    return "inStock";
  }

  if (stockEntries.length > 0 || typeof stock.stockLevel === "number") {
    return "outOfStock";
  }

  return stockStatus;
}
